// NewsML2CTS 形式のメモXMLを生成するユーティリティ
// 参考テンプレート: src/template/NewsML2CTS.xml

// 素材種別を英字スラッグへ正規化（ファイル名/ID生成と同じルール）
const materialKindToSlug = (kind) => {
  const raw = kind == null ? '' : String(kind).trim();
  const lower = raw.toLowerCase();
  if (raw === '記事') return 'article';
  if (raw === '写真' || raw === '画像') return 'photo';
  if (raw === 'ＥＰＳ' || lower === 'eps') return 'eps';
  if (raw === 'ＰＤＦ' || lower === 'pdf') return 'pdf';
  if (raw === 'エクセル' || lower === 'excel') return 'excel';
  if (raw === '動画' || lower === 'video') return 'video';
  return lower || 'article';
};

// スケジュールIDベースで themeDuid / newsItemId を生成（指定が無ければ従来の乱数ベース）
const buildIds = ({ scheduleId, materialKind, materialSerial, baseSeq, scheduleTimeStr, existingThemeDuid, existingNewsItemId }) => {
  // 既存IDが指定されている場合はそのまま使用（メモ編集時）
  if (existingThemeDuid && existingNewsItemId) {
    return {
      themeDuid: existingThemeDuid,
      newsItemId: existingNewsItemId,
    };
  }

  const slug = materialKindToSlug(materialKind);
  const serial = String(materialSerial || 1).padStart(4, '0');

  if (scheduleId) {
    // scheduleIdを「-」で分割し、最後の値を使用
    const shortScheduleId = String(scheduleId).split('-').pop();
    return {
      themeDuid: `NOTET-${shortScheduleId}`,
      newsItemId: `NOTEM-${shortScheduleId}-${slug}-${serial}`,
    };
  }

  return {
    themeDuid: `NOTET${scheduleTimeStr}`,
    newsItemId: `NOTEM${baseSeq}`,
  };
};

// NyuukouYotei はスケジュール開始日時を優先し、無い場合は登録予定日 00:00:00 を使用
const buildNyuukouYotei = ({ eventStart, memoRegistrationDate, fallbackDate, tz }) => {
  const isValidDate = (d) => d instanceof Date && !Number.isNaN(d.getTime());
  const pad = (n, len = 2) => String(n).padStart(len, '0');

  const start = eventStart ? new Date(eventStart) : null;
  if (isValidDate(start)) {
    const y = start.getFullYear();
    const m = pad(start.getMonth() + 1);
    const d = pad(start.getDate());
    const h = pad(start.getHours());
    const mi = pad(start.getMinutes());
    const s = pad(start.getSeconds());
    return `${y}${m}${d}T${h}${mi}${s}${tz}`;
  }

  const baseDate = memoRegistrationDate || `${fallbackDate.getFullYear()}-${pad(fallbackDate.getMonth() + 1)}-${pad(fallbackDate.getDate())}`;
  return `${String(baseDate).replace(/-/g, '')}T000000${tz}`;
};

/**
 * @param {Object} params
 * @param {string} params.themeName テーマ名
 * @param {string} params.themeContent テーマの内容
 * @param {string} params.memoType 種別 (例: NEC)
 * @param {string} params.memoPriority 重要度 (例: ベタ/トップ 等)
 * @param {string} params.memoRegistrationDate 登録予定日 YYYY-MM-DD
 * @param {string} params.memoCategory 分類 (例: 社会)
 * @param {string} params.reporterName 記者名（ByLine）
 * @param {Date|string} [params.eventStart] スケジュール開始日
 * @param {string} [params.username] ログインID（CreatorID）
 * @returns {string} NewsML2CTS 準拠のXML文字列（UTF-16BE宣言）
 */
export function buildMemoNewsML2CTS(params) {
  const {
    themeName = '',
    themeContent = '',
    memoType = 'NEC',
    memoPriority = 'ベタ',
    memoRegistrationDate = '',
    memoCategory = '01000000',
    reporterName = '未設定',
    eventStart,
    username = 'user',
    scheduleId,
    materialKind = '記事',
    materialSerial = 1,
    linkUrl = '',
    editInfo = '',
  } = params || {};

  const now = new Date();
  const pad = (n, len = 2) => String(n).padStart(len, '0');
  const ymd = (d) => `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
  const ymdSlash = (d) => `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())}`;
  const hhmmss = (d) => `${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
  const hhmmssColon = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  const tz = '+0900';

  const startDate = eventStart ? new Date(eventStart) : now;
  const dateId = ymd(startDate);
  const dateLine = `${ymdSlash(startDate)}`; // 例: 2025/10/07
  const dateAndTime = `${ymd(now)}T${hhmmss(now)}${tz}`; // 例: 20251007T102436+0900

  // NewsItemId / dhDuid をスケジュールIDベースで生成（指定が無ければ従来の乱数ベース）
  const baseSeq = `${ymd(now)}${hhmmss(now)}${String(Math.floor(Math.random() * 90) + 10)}`;
  const scheduleTimeStr = eventStart
    ? `${ymd(startDate)}${hhmmss(startDate)}`
    : (memoRegistrationDate
        ? `${String(memoRegistrationDate).replace(/-/g, '')}000000`
        : `${ymd(now)}${hhmmss(now)}`);
  const { newsItemId, themeDuid } = buildIds({
    scheduleId,
    materialKind,
    materialSerial,
    baseSeq,
    scheduleTimeStr,
  });

  const escapeXml = (s) => s == null ? '' : String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

  const nyuukouYotei = buildNyuukouYotei({
    eventStart,
    memoRegistrationDate,
    fallbackDate: startDate,
    tz,
  });

  // 重要度（数字）: 例としてベタ=1/トップ=5 等、暫定マッピング
  /*
  const urgencyMap = {
    'ベタ': '1',
    'トップ': '5',
    '段もの': '3',
    '取り置き可': '2',
    'その他': '2',
    '決まりもの': '1',
  };
  const urgencyFormalName = urgencyMap[memoPriority] || '1';
  */
  const urgencyMap = {
    'ベタ': '5',
    'トップ': '1',
    '段もの': '3',
    '取り置き可': '6',
    'その他': '7',
    '決まりもの': '8',
  };
  const urgencyFormalName = urgencyMap[memoPriority] || '5';

  // dhItemLine（何行想定か）: 暫定で空、必要なら算出
  const dhItemLineText = '';

  // dhNewsGenre はテーマの種別を使用
  const necGenre = memoType || 'ＮＥＣ';

  return `<?xml version="1.0" encoding="UTF-16BE"?>
<NewsML>
  <NewsEnvelope>
    <DateAndTime>${escapeXml(dateAndTime)}</DateAndTime>
    <NewsProduct FormalName="NEC-NewsML:1.0" Scheme="NecNewsProduct"/>
    <Priority FormalName="${escapeXml(urgencyFormalName)}" Scheme="IptcPriority"/>
  </NewsEnvelope>
  <NewsItem>
    <Identification>
      <NewsIdentifier>
        <ProviderId>daily-tohoku.co.jp</ProviderId>
        <DateId>${escapeXml(dateId)}</DateId>
        <NewsItemId>${escapeXml(newsItemId)}</NewsItemId>
        <RevisionId PreviousRevision="0" Update="N">1</RevisionId>
        <PublicIdentifier>urn:newsml:daily-tohoku.co.jp:${escapeXml(dateId)}:${escapeXml(newsItemId)}:1</PublicIdentifier>
      </NewsIdentifier>
    </Identification>
    <NewsManagement>
      <NewsItemType FormalName="News"/>
      <FirstCreated>${escapeXml(dateAndTime)}</FirstCreated>
      <ThisRevisionCreated>${escapeXml(dateAndTime)}</ThisRevisionCreated>
      <Status FormalName="Usable" Scheme="IptcStatus"/>
      <Urgency FormalName="${escapeXml(urgencyFormalName)}" Scheme=""/>
    </NewsManagement>
    <NewsComponent Duid="NC001-root" xml:lang="ja-JP" EquivalentsList="no">
      <BasisForChoice>./Role/@FormalName</BasisForChoice>
      <NewsLines>
        <HeadLine>${escapeXml(themeName || '')}</HeadLine>
        <ByLine>${escapeXml(reporterName)}</ByLine>
        <DateLine>${escapeXml(dateLine)}</DateLine>
        <CopyrightLine>Copyright(C) ${escapeXml(String(now.getFullYear()))} (c)DAILYTOHOKU All rights reserved.</CopyrightLine>
        <NewsLine>
          <NewsLineType FormalName="EditInfo" Scheme="NskNewsLineType"/>
          <NewsLineText>${escapeXml(editInfo || '')}</NewsLineText>
        </NewsLine>
      </NewsLines>
      <AdministrativeMetadata>
        <Provider>
          <Party FormalName="DAILYTOHOKU"/>
        </Provider>
        <Creator>
          <Party FormalName="DAILYTOHOKU"/>
        </Creator>
      </AdministrativeMetadata>
      <RightsMetadata>
        <Copyright>
          <CopyrightHolder>THE DAILY TOHOKU SHIMBUN</CopyrightHolder>
          <CopyrightDate>${escapeXml(String(now.getFullYear()))}</CopyrightDate>
        </Copyright>
      </RightsMetadata>
      <DescriptiveMetadata>
        <Language FormalName="ja" Scheme="ISO639"/>
        <SubjectCode>
          <Subject FormalName="${escapeXml(memoCategory || '01000000')}"/>
        </SubjectCode>
      </DescriptiveMetadata>
      <Metadata>
        <MetadataType FormalName="NecProperties" Scheme="NecMetadataTypes" />
        <Property FormalName="IsDesk" Value="true" Scheme="NecProperties"/>
        <Property FormalName="FromHotFolder" Value="on" Scheme="NecProperties"/>
        <Property FormalName="LinkURL" Value="${escapeXml(linkUrl || '')}" Scheme="NecProperties"/>
        <Property FormalName="CreatorID" Value="${escapeXml(username)}" Scheme="NecProperties"/>
        <Property FormalName="MaterialKind" Value="${escapeXml(materialKind)}"/>
        <Property FormalName="NyuukouYotei" Value="${escapeXml(nyuukouYotei)}"/>
      </Metadata>
      <ContentItem>
        <MediaType FormalName="Text"/>
        <Format FormalName="KdDHKmMLv1.0"/>
        <MimeType FormalName="text/xml"/>
        <DataContent>
          <InContent version="1.0">
            <InMetadata>
              <InAdminInfo>
                <InProductId></InProductId>
                <InDateTime></InDateTime>
                <InFormat>
                  <InFormatType>フルタグ書式</InFormatType>
                  <InFormatComment>棒</InFormatComment>
                </InFormat>
                <InModifyInfo>新規</InModifyInfo>
              </InAdminInfo>
              <InCategoryInfo>
                <InNewsCategory>
                  <InNewsGenre>メモ</InNewsGenre>
                </InNewsCategory>
                <InNewsLine>
                  <InHeadLine>${escapeXml(themeName || '')}</InHeadLine>
                </InNewsLine>
              </InCategoryInfo>
              <InNewsInfo>
                <InNewsKindInfo>
                  <InNewsKind>メモ</InNewsKind>
                </InNewsKindInfo>
              </InNewsInfo>
            </InMetadata>
            <InData>
              <KdDHKmML>
                <dhitem dhitem-type="text">
                  <dhRegisterName></dhRegisterName>
                  <dhol>
                    <dhli dhDuid="${escapeXml(themeDuid)}" dhli-type="main">
                      <dhListNo>${escapeXml(themeName || '')}</dhListNo>
                      <dhp/>
                      <dhp>${escapeXml(themeContent || '')}</dhp>
                      <dhMemoCaution>
                        <dhp></dhp>
                      </dhMemoCaution>
                      <dhol>
                        <dhli dhDuid="${escapeXml(newsItemId)}" dhli-type="spare" dhrole="本記">
                          <dhRelateInformation>
                            <dhTimeTable></dhTimeTable>
                            <dhNewsObject>
                              <dhNewsCategory>
                                <dhNewsGenre>${escapeXml(necGenre)}</dhNewsGenre>
                                <dhNewsNo></dhNewsNo>
                              </dhNewsCategory>
                            </dhNewsObject>
                            <dhItemLine>${escapeXml(dhItemLineText)}</dhItemLine>
                            <dhItemLine/>
                          </dhRelateInformation>
                        </dhli>
                      </dhol>
                    </dhli>
                  </dhol>
                </dhitem>
              </KdDHKmML>
            </InData>
          </InContent>
        </DataContent>
      </ContentItem>
    </NewsComponent>
  </NewsItem>
</NewsML>`;
}

/**
 * 記事素材用 NewsML2CTS
 * materialHeadline を InHeadLine と dhRelateInformation の見出し系に反映
 */
export function buildArticleNewsML2CTS(params) {
  const {
    themeName = '',
    themeContent = '',
    memoPriority = 'ベタ',
    memoRegistrationDate = '',
    memoCategory = '01000000',
    reporterName = '未設定',
    eventStart,
    username = 'user',
    materialHeadline = '',
    materialKind = '記事',
    materialLines = '',
    scheduleId,
    materialSerial = 1,
    memoType = 'NEC',
    existingThemeDuid,
    existingNewsItemId,
    linkUrl = '',
    editInfo = '',
  } = params || {};

  const now = new Date();
  const pad = (n, len = 2) => String(n).padStart(len, '0');
  const ymd = (d) => `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
  const ymdSlash = (d) => `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())}`;
  const hhmmss = (d) => `${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
  const tz = '+0900';

  const startDate = eventStart ? new Date(eventStart) : now;
  const dateId = ymd(startDate);
  const dateLine = `${ymdSlash(startDate)}`;
  const dateAndTime = `${ymd(now)}T${hhmmss(now)}${tz}`;
  const baseSeq = `${ymd(now)}${hhmmss(now)}${String(Math.floor(Math.random() * 90) + 10)}`;
  // themeDuid をスケジュールIDベースで生成（指定が無ければスケジュール日時ベース）
  const scheduleTimeStr = eventStart
    ? `${ymd(startDate)}${hhmmss(startDate)}`
    : (memoRegistrationDate
        ? `${String(memoRegistrationDate).replace(/-/g, '')}000000`
        : `${ymd(now)}${hhmmss(now)}`);
  const { newsItemId, themeDuid } = buildIds({
    scheduleId,
    materialKind: '記事',
    materialSerial,
    baseSeq,
    scheduleTimeStr,
    existingThemeDuid,
    existingNewsItemId,
  });
  const escapeXml = (s) => s == null ? '' : String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

  const nyuukouYotei = buildNyuukouYotei({
    eventStart,
    memoRegistrationDate,
    fallbackDate: startDate,
    tz,
  });
//  const urgencyMap = { 'ベタ': '1', 'トップ': '5', '段もの': '3', '取り置き可': '2', 'その他': '2', '決まりもの': '1' };
//  const urgencyFormalName = urgencyMap[memoPriority] || '1';
  const urgencyMap = { 'ベタ': '5', 'トップ': '1', '段もの': '3', '取り置き可': '6', 'その他': '7', '決まりもの': '8' };
  const urgencyFormalName = urgencyMap[memoPriority] || '5';
  const necGenre = memoType || 'ＮＥＣ';
  const dhItemLineText = materialLines == null ? '' : String(materialLines);

  return `<?xml version="1.0" encoding="UTF-16BE"?>
<NewsML>
  <NewsEnvelope>
    <DateAndTime>${escapeXml(dateAndTime)}</DateAndTime>
    <NewsProduct FormalName="NEC-NewsML:1.0" Scheme="NecNewsProduct"/>
    <Priority FormalName="${escapeXml(urgencyFormalName)}" Scheme="IptcPriority"/>
  </NewsEnvelope>
  <NewsItem>
    <Identification>
      <NewsIdentifier>
        <ProviderId>daily-tohoku.co.jp</ProviderId>
        <DateId>${escapeXml(dateId)}</DateId>
        <NewsItemId>${escapeXml(newsItemId)}</NewsItemId>
        <RevisionId PreviousRevision="0" Update="N">1</RevisionId>
        <PublicIdentifier>urn:newsml:daily-tohoku.co.jp:${escapeXml(dateId)}:${escapeXml(newsItemId)}:1</PublicIdentifier>
      </NewsIdentifier>
    </Identification>
    <NewsManagement>
      <NewsItemType FormalName="News"/>
      <FirstCreated>${escapeXml(dateAndTime)}</FirstCreated>
      <ThisRevisionCreated>${escapeXml(dateAndTime)}</ThisRevisionCreated>
      <Status FormalName="Usable" Scheme="IptcStatus"/>
      <Urgency FormalName="${escapeXml(urgencyFormalName)}" Scheme=""/>
    </NewsManagement>
    <NewsComponent Duid="NC001-root" xml:lang="ja-JP" EquivalentsList="no">
      <BasisForChoice>./Role/@FormalName</BasisForChoice>
      <NewsLines>
        <HeadLine>${escapeXml(materialHeadline || '')}</HeadLine>
        <ByLine>${escapeXml(reporterName)}</ByLine>
        <DateLine>${escapeXml(dateLine)}</DateLine>
        <CopyrightLine>Copyright(C) ${escapeXml(String(now.getFullYear()))} (c)DAILYTOHOKU All rights reserved.</CopyrightLine>
        <NewsLine>
          <NewsLineType FormalName="EditInfo" Scheme="NskNewsLineType"/>
          <NewsLineText>${escapeXml(editInfo || '')}</NewsLineText>
        </NewsLine>
      </NewsLines>
      <AdministrativeMetadata>
        <Provider>
          <Party FormalName="DAILYTOHOKU"/>
        </Provider>
        <Creator>
          <Party FormalName="DAILYTOHOKU"/>
        </Creator>
      </AdministrativeMetadata>
      <RightsMetadata>
        <Copyright>
          <CopyrightHolder>THE DAILY TOHOKU SHIMBUN</CopyrightHolder>
          <CopyrightDate>${escapeXml(String(now.getFullYear()))}</CopyrightDate>
        </Copyright>
      </RightsMetadata>
      <DescriptiveMetadata>
        <Language FormalName="ja" Scheme="ISO639"/>
        <SubjectCode>
          <Subject FormalName="${escapeXml(memoCategory || '01000000')}"/>
        </SubjectCode>
      </DescriptiveMetadata>
      <Metadata>
        <MetadataType FormalName="NecProperties" Scheme="NecMetadataTypes" />
        <Property FormalName="IsDesk" Value="true" Scheme="NecProperties"/>
        <Property FormalName="FromHotFolder" Value="on" Scheme="NecProperties"/>
        <Property FormalName="LinkURL" Value="${escapeXml(linkUrl || '')}" Scheme="NecProperties"/>
        <Property FormalName="CreatorID" Value="${escapeXml(username)}" Scheme="NecProperties"/>
        <Property FormalName="MaterialKind" Value="${escapeXml(materialKind)}"/>
        <Property FormalName="NyuukouYotei" Value="${escapeXml(nyuukouYotei)}"/>
      </Metadata>
      <ContentItem>
        <MediaType FormalName="Text"/>
        <Format FormalName="KdDHKmMLv1.0"/>
        <MimeType FormalName="text/xml"/>
        <DataContent>
          <InContent version="1.0">
            <InMetadata>
              <InAdminInfo>
                <InProductId></InProductId>
                <InDateTime></InDateTime>
                <InFormat>
                  <InFormatType>フルタグ書式</InFormatType>
                  <InFormatComment>棒</InFormatComment>
                </InFormat>
                <InModifyInfo>新規</InModifyInfo>
              </InAdminInfo>
              <InCategoryInfo>
                <InNewsCategory>
                  <InNewsGenre>メモ</InNewsGenre>
                </InNewsCategory>
                <InNewsLine>
                  <InHeadLine>${escapeXml(materialHeadline || '')}</InHeadLine>
                </InNewsLine>
              </InCategoryInfo>
              <InNewsInfo>
                <InNewsKindInfo>
                  <InNewsKind>メモ</InNewsKind>
                </InNewsKindInfo>
              </InNewsInfo>
            </InMetadata>
            <InData>
              <KdDHKmML>
                <dhitem dhitem-type="text">
                  <dhRegisterName></dhRegisterName>
                  <dhol>
                    <dhli dhDuid="${escapeXml(themeDuid)}" dhli-type="main">
                      <dhListNo>${escapeXml(themeName || '')}</dhListNo>
                      <dhp/>
                      <dhp>${escapeXml(themeContent || '')}</dhp>
                      <dhMemoCaution>
                        <dhp></dhp>
                      </dhMemoCaution>
                      <dhol>
                        <dhli dhDuid="${escapeXml(newsItemId)}" dhli-type="spare" dhrole="本記">
                          <dhRelateInformation>
                            <dhTimeTable></dhTimeTable>
                            <dhNewsObject>
                              <dhNewsCategory>
                                <dhNewsGenre>${escapeXml(necGenre)}</dhNewsGenre>
                                <dhNewsNo></dhNewsNo>
                              </dhNewsCategory>
                            </dhNewsObject>
                            <dhItemLine>${escapeXml(dhItemLineText)}</dhItemLine>
                            <dhItemLine/>
                          </dhRelateInformation>
                        </dhli>
                      </dhol>
                    </dhli>
                  </dhol>
                </dhitem>
              </KdDHKmML>
            </InData>
          </InContent>
        </DataContent>
      </ContentItem>
    </NewsComponent>
  </NewsItem>
</NewsML>`;
}

/**
 * 写真/EPS/PDF 用 NewsML2CTS（テンプレは写真ベース）
 * materialKind を NecProperties.MaterialKind に反映
 */
export function buildPhotoNewsML2CTS(params) {
  const {
    themeName = '',
    themeContent = '',
    memoPriority = 'ベタ',
    memoRegistrationDate = '',
    memoCategory = '01000000',
    reporterName = '未設定',
    eventStart,
    username = 'user',
    materialHeadline = '',
    materialKind = '写真', // '画像' 'EPS' 'PDF' を含む
    scheduleId,
    materialSerial = 1,
    memoType = 'NEC',
    existingThemeDuid,
    existingNewsItemId,
    linkUrl = '',
    editInfo = '',
  } = params || {};

  const now = new Date();
  const pad = (n, len = 2) => String(n).padStart(len, '0');
  const ymd = (d) => `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
  const ymdSlash = (d) => `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())}`;
  const hhmmss = (d) => `${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
  const tz = '+0900';

  const startDate = eventStart ? new Date(eventStart) : now;
  const dateId = ymd(startDate);
  const dateLine = `${ymdSlash(startDate)}`;
  const dateAndTime = `${ymd(now)}T${hhmmss(now)}${tz}`;
  const baseSeq = `${ymd(now)}${hhmmss(now)}${String(Math.floor(Math.random() * 90) + 10)}`;
  // themeDuid / newsItemId をスケジュールIDベースで生成（指定が無ければスケジュール日時ベース）
  const scheduleTimeStr = eventStart
    ? `${ymd(startDate)}${hhmmss(startDate)}`
    : (memoRegistrationDate
        ? `${String(memoRegistrationDate).replace(/-/g, '')}000000`
        : `${ymd(now)}${hhmmss(now)}`);
  const { newsItemId, themeDuid } = buildIds({
    scheduleId,
    materialKind,
    materialSerial,
    baseSeq,
    scheduleTimeStr,
    existingThemeDuid,
    existingNewsItemId,
  });
  const escapeXml = (s) => s == null ? '' : String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

  const nyuukouYotei = buildNyuukouYotei({
    eventStart,
    memoRegistrationDate,
    fallbackDate: startDate,
    tz,
  });
//  const urgencyMap = { 'ベタ': '1', 'トップ': '5', '段もの': '3', '取り置き可': '2', 'その他': '2', '決まりもの': '1' };
//  const urgencyFormalName = urgencyMap[memoPriority] || '1';
  const urgencyMap = { 'ベタ': '5', 'トップ': '1', '段もの': '3', '取り置き可': '6', 'その他': '7', '決まりもの': '8' };
  const urgencyFormalName = urgencyMap[memoPriority] || '5';
  const necGenre = memoType || 'ＮＥＣ';

  return `<?xml version="1.0" encoding="UTF-16BE"?>
<NewsML>
  <NewsEnvelope>
    <DateAndTime>${escapeXml(dateAndTime)}</DateAndTime>
    <NewsProduct FormalName="NEC-NewsML:1.0" Scheme="NecNewsProduct"/>
    <Priority FormalName="${escapeXml(urgencyFormalName)}" Scheme="IptcPriority"/>
  </NewsEnvelope>
  <NewsItem>
    <Identification>
      <NewsIdentifier>
        <ProviderId>daily-tohoku.co.jp</ProviderId>
        <DateId>${escapeXml(dateId)}</DateId>
        <NewsItemId>${escapeXml(newsItemId)}</NewsItemId>
        <RevisionId PreviousRevision="0" Update="N">1</RevisionId>
        <PublicIdentifier>urn:newsml:daily-tohoku.co.jp:${escapeXml(dateId)}:${escapeXml(newsItemId)}:1</PublicIdentifier>
      </NewsIdentifier>
    </Identification>
    <NewsManagement>
      <NewsItemType FormalName="News"/>
      <FirstCreated>${escapeXml(dateAndTime)}</FirstCreated>
      <ThisRevisionCreated>${escapeXml(dateAndTime)}</ThisRevisionCreated>
      <Status FormalName="Usable" Scheme="IptcStatus"/>
      <Urgency FormalName="${escapeXml(urgencyFormalName)}" Scheme=""/>
    </NewsManagement>
    <NewsComponent Duid="NC001-root" xml:lang="ja-JP" EquivalentsList="no">
      <BasisForChoice>./Role/@FormalName</BasisForChoice>
      <NewsLines>
        <HeadLine>${escapeXml(materialHeadline || '')}</HeadLine>
        <ByLine>${escapeXml(reporterName)}</ByLine>
        <DateLine>${escapeXml(dateLine)}</DateLine>
        <CopyrightLine>Copyright(C) ${escapeXml(String(now.getFullYear()))} (c)DAILYTOHOKU All rights reserved.</CopyrightLine>
        <NewsLine>
          <NewsLineType FormalName="EditInfo" Scheme="NskNewsLineType"/>
          <NewsLineText>${escapeXml(editInfo || '')}</NewsLineText>
        </NewsLine>
      </NewsLines>
      <AdministrativeMetadata>
        <Provider>
          <Party FormalName="DAILYTOHOKU"/>
        </Provider>
        <Creator>
          <Party FormalName="DAILYTOHOKU"/>
        </Creator>
      </AdministrativeMetadata>
      <RightsMetadata>
        <Copyright>
          <CopyrightHolder>THE DAILY TOHOKU SHIMBUN</CopyrightHolder>
          <CopyrightDate>${escapeXml(String(now.getFullYear()))}</CopyrightDate>
        </Copyright>
      </RightsMetadata>
      <DescriptiveMetadata>
        <Language FormalName="ja" Scheme="ISO639"/>
        <SubjectCode>
          <Subject FormalName="${escapeXml(memoCategory || '01000000')}"/>
        </SubjectCode>
      </DescriptiveMetadata>
      <Metadata>
        <MetadataType FormalName="NecProperties" Scheme="NecMetadataTypes" />
        <Property FormalName="IsDesk" Value="true" Scheme="NecProperties"/>
        <Property FormalName="FromHotFolder" Value="on" Scheme="NecProperties"/>
        <Property FormalName="LinkURL" Value="${escapeXml(linkUrl || '')}" Scheme="NecProperties"/>
        <Property FormalName="CreatorID" Value="${escapeXml(username)}" Scheme="NecProperties"/>
        <Property FormalName="MaterialKind" Value="${escapeXml(materialKind)}"/>
        <Property FormalName="NyuukouYotei" Value="${escapeXml(nyuukouYotei)}"/>
      </Metadata>
      <ContentItem>
        <MediaType FormalName="Text"/>
        <Format FormalName="KdDHKmMLv1.0"/>
        <MimeType FormalName="text/xml"/>
        <DataContent>
          <InContent version="1.0">
            <InMetadata>
              <InAdminInfo>
                <InProductId></InProductId>
                <InDateTime></InDateTime>
                <InFormat>
                  <InFormatType>フルタグ書式</InFormatType>
                  <InFormatComment>棒</InFormatComment>
                </InFormat>
                <InModifyInfo>新規</InModifyInfo>
              </InAdminInfo>
              <InCategoryInfo>
                <InNewsCategory>
                  <InNewsGenre>メモ</InNewsGenre>
                </InNewsCategory>
                <InNewsLine>
                  <InHeadLine>${escapeXml(materialHeadline || '')}</InHeadLine>
                </InNewsLine>
              </InCategoryInfo>
              <InNewsInfo>
                <InNewsKindInfo>
                  <InNewsKind>メモ</InNewsKind>
                </InNewsKindInfo>
              </InNewsInfo>
            </InMetadata>
            <InData>
              <KdDHKmML>
                <dhitem dhitem-type="text">
                  <dhRegisterName></dhRegisterName>
                  <dhol>
                    <dhli dhDuid="${escapeXml(themeDuid)}" dhli-type="main">
                      <dhListNo>${escapeXml(themeName || '')}</dhListNo>
                      <dhp/>
                      <dhp>${escapeXml(themeContent || '')}</dhp>
                      <dhMemoCaution>
                        <dhp></dhp>
                      </dhMemoCaution>
                      <dhol>
                        <dhli dhDuid="${escapeXml(newsItemId)}" dhli-type="spare" dhrole="本記">
                          <dhRelateInformation>
                            <dhTimeTable></dhTimeTable>
                            <dhNewsObject>
                              <dhNewsCategory>
                                <dhNewsGenre>${escapeXml(necGenre)}</dhNewsGenre>
                                <dhNewsNo></dhNewsNo>
                              </dhNewsCategory>
                            </dhNewsObject>
                            <dhItemLine/>
                          </dhRelateInformation>
                        </dhli>
                      </dhol>
                    </dhli>
                  </dhol>
                </dhitem>
              </KdDHKmML>
            </InData>
          </InContent>
        </DataContent>
      </ContentItem>
    </NewsComponent>
  </NewsItem>
</NewsML>`;
}
